import { useEffect, useState, useCallback, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { notification } from 'antd';
import { useKeycloak } from '@react-keycloak/web';

import {
  getExpired,
  getMenuAccess,
  getRefreshToken,
  getToken,
  removeUserSession,
  setRefreshUserSession,
} from '../../utils/common';
import logger from '../../utils/logger';

const AutoLogout = () => {
  const { keycloak } = useKeycloak();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const idleTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [permissionCheckInterval, setPermissionCheckInterval] = useState(null);
  const idleTokenRefreshIntervalRef = useRef(null);

  // Constants for timeouts (in milliseconds)
  const IDLE_WARNING_TIME = 28 * 60 * 1000; // 28 minutes
  const IDLE_TIMEOUT_TIME = 30 * 60 * 1000; // 30 minutes
  const PERMISSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes (instead of every second)
  const TOKEN_REFRESH_BUFFER = 1 * 60 * 1000; // 1 minute before expiry (for 5-min tokens)
  const TOKEN_CHECK_INTERVAL = 60 * 1000; // Check every 60 seconds (reduced from 15 seconds)
  const IDLE_TOKEN_REFRESH_INTERVAL = 30 * 1000; // Check every 30 seconds when idle (reduced from 10 seconds)

  // Optimized permission checking - only check every 5 minutes instead of every second
  const checkPermissions = useCallback(() => {
    const token = getToken();
    if (token) {
      getMenuAccess();
    }
  }, []);

  // Check if refresh token is valid
  const isRefreshTokenValid = useCallback(() => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      // Check if refresh token is expired (assuming it's a JWT)
      const tokenParts = refreshToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        return payload.exp > currentTime;
      }
      return true; // If not JWT format, assume valid
    } catch (error) {
      logger.error('Error validating refresh token:', error);
      return false;
    }
  }, []);

  // Handle user logout
  const handleLogout = useCallback((message = 'Session expired due to inactivity.') => {
    notification.warning({
      message: 'Session Expired',
      description: message,
      placement: 'top',
      duration: 3,
    });

    removeUserSession();
    globalThis.location.href = '/login';
  }, []);

  // Enhanced token refresh using Keycloak's built-in methods
  const handleTokenRefresh = useCallback(async () => {
    if (!keycloak || !keycloak.authenticated) {
      return;
    }

    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing) {
      return; // Skip silently to reduce console spam
    }

    setIsRefreshing(true);

    try {
      // Check token expiration manually first
      const token = keycloak.token;
      let shouldRefresh = false;

      if (token) {
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = payload.exp - currentTime;

            // Refresh if token expires within 1 minute (60 seconds)
            if (timeUntilExpiry <= 60) {
              shouldRefresh = true;
            }
          }
        } catch (e) {
          shouldRefresh = true; // Refresh if we can't parse the token
        }
      } else {
        shouldRefresh = true; // Refresh if no token
      }

      // Use Keycloak's updateToken method with 1-minute buffer (60 seconds)
      const refreshed = await keycloak.updateToken(60);

      if (refreshed) {
        // Update session storage with new tokens
        if (keycloak.token) {
          sessionStorage.setItem('token', keycloak.token);
        }
        if (keycloak.refreshToken) {
          sessionStorage.setItem('refreshToken', keycloak.refreshToken);
        }

        // Update expired timestamp
        if (keycloak.tokenParsed && keycloak.tokenParsed.exp) {
          sessionStorage.setItem('expired', keycloak.tokenParsed.exp.toString());
        }

        // Check permissions after successful refresh
        checkPermissions();
      }
    } catch (error) {
      logger.error('❌ Token refresh failed:', error);

      // Check if refresh token is still valid
      if (!isRefreshTokenValid()) {
        handleLogout('Session expired. Please login again.');
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [keycloak, handleLogout, isRefreshing, isRefreshTokenValid, checkPermissions]);

  // Reset idle timers
  const resetIdleTimers = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);

    // Clear existing timeouts
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (idleTokenRefreshIntervalRef.current) clearInterval(idleTokenRefreshIntervalRef.current);

    // Hide warning if showing
    setShowIdleWarning(false);

    // Set warning timeout (28 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      setShowIdleWarning(true);
    }, IDLE_WARNING_TIME);

    // Set logout timeout (30 minutes)
    idleTimeoutRef.current = setTimeout(() => {
      handleLogout();
    }, IDLE_TIMEOUT_TIME);

    // Set up idle token refresh (every 30 seconds when idle)
    idleTokenRefreshIntervalRef.current = setInterval(() => {
      if (keycloak && keycloak.authenticated) {
        handleTokenRefresh();
      }
    }, IDLE_TOKEN_REFRESH_INTERVAL);
  }, [handleLogout, keycloak, handleTokenRefresh]);

  // Handle user activity with enhanced document reading support
  const handleUserActivity = useCallback(
    (event) => {
      resetIdleTimers();

      // Log document reading activities for debugging
      if (event && event.type) {
        const documentReadingEvents = ['scroll', 'wheel', 'keydown', 'keyup', 'selectstart', 'selectionchange'];
        if (documentReadingEvents.includes(event.type)) {
        }
      }

      // Also trigger token refresh on user activity to ensure tokens stay fresh during analysis
      if (keycloak && keycloak.authenticated) {
        // Debounce token refresh to avoid excessive calls
        if (!isRefreshing) {
          setTimeout(() => {
            handleTokenRefresh();
          }, 1000); // Wait 1 second after activity
        }
      }
    },
    [resetIdleTimers, keycloak, isRefreshing, handleTokenRefresh]
  );

  // Handle continue session
  const handleContinueSession = useCallback(() => {
    setShowIdleWarning(false);
    resetIdleTimers();
    notification.success({
      message: 'Session Extended',
      description: 'Your session has been extended.',
      placement: 'top',
      duration: 2,
    });
  }, [resetIdleTimers]);

  // Handle logout from warning
  const handleLogoutFromWarning = useCallback(() => {
    setShowIdleWarning(false);
    handleLogout('You have been logged out due to inactivity.');
  }, [handleLogout]);

  useEffect(() => {
    // Set up Keycloak event handlers for automatic token refresh
    if (keycloak) {
      // Handle token expiration automatically
      keycloak.onTokenExpired = () => {
        handleTokenRefresh();
      };

      // Handle authentication success
      keycloak.onAuthSuccess = () => {
        // Update session storage with fresh tokens
        if (keycloak.token) {
          sessionStorage.setItem('token', keycloak.token);
        }
        if (keycloak.refreshToken) {
          sessionStorage.setItem('refreshToken', keycloak.refreshToken);
        }
        if (keycloak.tokenParsed && keycloak.tokenParsed.exp) {
          sessionStorage.setItem('expired', keycloak.tokenParsed.exp.toString());
        }
      };

      // Handle authentication errors
      keycloak.onAuthError = (error) => {
        logger.error('❌ Authentication error:', error);
        handleLogout('Authentication failed. Please login again.');
      };

      // Handle authentication logout
      keycloak.onAuthLogout = () => {
        removeUserSession();
      };
    }

    // Initialize timers
    resetIdleTimers();

    // Immediate token refresh check on mount
    if (keycloak && keycloak.authenticated) {
      handleTokenRefresh();
    }

    // Set up permission checking interval (every 5 minutes instead of every second)
    const permissionInterval = setInterval(checkPermissions, PERMISSION_CHECK_INTERVAL);
    setPermissionCheckInterval(permissionInterval);

    // Set up token refresh check (every 60 seconds)
    // This runs independently of user activity to ensure tokens don't expire during idle periods
    const tokenCheckInterval = setInterval(() => {
      if (keycloak && keycloak.authenticated) {
        handleTokenRefresh();
      }
    }, TOKEN_CHECK_INTERVAL); // Check every 60 seconds

    // Enhanced event listeners for user activity (including document reading)
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'wheel', // Mouse wheel scrolling (important for document reading)
      'keydown', // Arrow keys, page up/down, space bar
      'keyup', // Key release
      'focus', // Window/tab focus
      'blur', // Window/tab blur
      'visibilitychange', // Tab visibility changes
      'resize', // Window resizing
      'contextmenu', // Right-click menu
      'selectstart', // Text selection start
      'selectionchange', // Text selection changes
    ];
    events.forEach((event) => {
      globalThis.addEventListener(event, handleUserActivity, true);
    });

    // Use pagehide instead of deprecated unload event and beforeunload
    // beforeunload prevents back/forward cache restoration, so we only use pagehide
    const handlePageHide = () => {
      if (!isRefreshing && keycloak && keycloak.authenticated) {
        handleTokenRefresh();
      }
    };

    globalThis.addEventListener('pagehide', handlePageHide);

    return () => {
      // Cleanup
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (permissionInterval) clearInterval(permissionInterval);
      if (tokenCheckInterval) clearInterval(tokenCheckInterval);
      if (idleTokenRefreshIntervalRef.current) clearInterval(idleTokenRefreshIntervalRef.current);

      events.forEach((event) => {
        globalThis.removeEventListener(event, handleUserActivity, true);
      });

      globalThis.removeEventListener('pagehide', handlePageHide);
    };
  }, [keycloak]); // Simplified dependency array to prevent infinite loops

  return (
    <>
      {/* Idle Warning Dialog */}
      <Dialog open={showIdleWarning} onClose={handleContinueSession} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle>
          <Typography variant="h6" color="warning.main">
            Session Timeout Warning
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              You have been inactive for 28 minutes. Your session will expire in 2 minutes.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Continue Session" to stay logged in, or you will be automatically logged out.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutFromWarning} color="error" variant="outlined">
            Logout Now
          </Button>
          <Button onClick={handleContinueSession} color="primary" variant="contained">
            Continue Session
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AutoLogout;
